import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { dailyReportAPI, employeeAPI } from '../services/api';
import './DailyReport.css';

const DailyReport = () => {
  const { isPrivileged } = useAuth();

  // Tab: employee sees 'submit' & 'history'; privileged sees 'all'
  const [tab, setTab] = useState(isPrivileged ? 'all' : 'submit');

  // ── SHARED STATE ─────────────────────────────────────────────
  const [alert, setAlert] = useState(null); // { type, msg }
  const [viewReport, setViewReport] = useState(null); // modal detail
  const [reviewModal, setReviewModal] = useState(null); // { report, status }

  const showAlert = (type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 4000);
  };

  // ── SUBMIT FORM STATE ────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ date: today, taskId: '', report: '', hoursWorked: '' });
  const [myTasks, setMyTasks] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // ── HISTORY STATE ────────────────────────────────────────────
  const [history, setHistory] = useState([]);
  const [histPage, setHistPage] = useState(1);
  const [histMeta, setHistMeta] = useState({});
  const [histFrom, setHistFrom] = useState('');
  const [histTo, setHistTo] = useState('');
  const [histLoading, setHistLoading] = useState(false);

  // ── ALL REPORTS STATE (admin) ────────────────────────────────
  const [reports, setReports] = useState([]);
  const [rptPage, setRptPage] = useState(1);
  const [rptMeta, setRptMeta] = useState({});
  const [rptFrom, setRptFrom] = useState('');
  const [rptTo, setRptTo] = useState('');
  const [rptEmployee, setRptEmployee] = useState('');
  const [rptLoading, setRptLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewStatus, setReviewStatus] = useState('reviewed');

  // ── FETCH: my tasks for dropdown ─────────────────────────────
  useEffect(() => {
    if (!isPrivileged) {
      dailyReportAPI.getMyTasks()
        .then(r => setMyTasks(r?.data || r || []))
        .catch(() => {});
    }
  }, [isPrivileged]);

  // ── FETCH: employee list for admin filter ────────────────────
  useEffect(() => {
    if (isPrivileged) {
      employeeAPI.getAll()
        .then(r => setEmployees(r?.data || r || []))
        .catch(() => {});
    }
  }, [isPrivileged]);

  // ── FETCH: history ───────────────────────────────────────────
  const fetchHistory = useCallback(async (page = 1) => {
    setHistLoading(true);
    try {
      const params = { page, limit: 15 };
      if (histFrom) params.from = histFrom;
      if (histTo) params.to = histTo;
      const res = await dailyReportAPI.getMy(params);
      setHistory(res?.data || []);
      setHistMeta({ total: res?.total, page: res?.page, pages: res?.pages });
      setHistPage(page);
    } catch (e) {
      showAlert('error', 'History load nahi hui');
    } finally {
      setHistLoading(false);
    }
  }, [histFrom, histTo]);

  useEffect(() => {
    if (tab === 'history') fetchHistory(1);
  }, [tab, fetchHistory]);

  // ── FETCH: all reports (admin) ───────────────────────────────
  const fetchReports = useCallback(async (page = 1) => {
    setRptLoading(true);
    try {
      const params = { page, limit: 15 };
      if (rptFrom) params.from = rptFrom;
      if (rptTo) params.to = rptTo;
      if (rptEmployee) params.employeeId = rptEmployee;
      const res = await dailyReportAPI.getAll(params);
      setReports(res?.data || []);
      setRptMeta({ total: res?.total, page: res?.page, pages: res?.pages });
      setRptPage(page);
    } catch (e) {
      showAlert('error', 'Reports load nahi hue');
    } finally {
      setRptLoading(false);
    }
  }, [rptFrom, rptTo, rptEmployee]);

  useEffect(() => {
    if (tab === 'all') fetchReports(1);
  }, [tab, fetchReports]);

  // ── SUBMIT REPORT ────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        date: form.date,
        report: form.report,
      };
      if (form.taskId) payload.taskId = form.taskId;
      if (form.hoursWorked !== '') payload.hoursWorked = Number(form.hoursWorked);

      await dailyReportAPI.submit(payload);
      showAlert('success', 'Report successfully submit ho gayi!');
      setForm({ date: today, taskId: '', report: '', hoursWorked: '' });
    } catch (err) {
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('already')) {
        showAlert('info', 'Aaj ki report pehle se hai — edit ho gayi!');
      } else {
        showAlert('error', msg || 'Report submit nahi hui, dobara try karo');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── REVIEW SUBMIT ────────────────────────────────────────────
  const handleReview = async () => {
    if (!reviewModal) return;
    try {
      await dailyReportAPI.review(reviewModal._id, { status: reviewStatus, reviewNote });
      showAlert('success', `Report ${reviewStatus === 'approved' ? 'approve' : 'review'} ho gayi`);
      setReviewModal(null);
      setReviewNote('');
      fetchReports(rptPage);
    } catch (e) {
      showAlert('error', 'Review submit nahi hua');
    }
  };

  // ── DELETE ───────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Yeh report delete karna chahte ho?')) return;
    try {
      await dailyReportAPI.delete(id);
      showAlert('success', 'Report delete ho gayi');
      if (isPrivileged) fetchReports(rptPage);
      else fetchHistory(histPage);
    } catch (e) {
      showAlert('error', 'Delete nahi hua');
    }
  };

  // ── HELPERS ──────────────────────────────────────────────────
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const StatusBadge = ({ status }) => {
    const map = {
      submitted: { label: 'Submitted', icon: 'fa-clock' },
      reviewed:  { label: 'Reviewed',  icon: 'fa-eye' },
      approved:  { label: 'Approved',  icon: 'fa-check-circle' },
    };
    const s = map[status] || map.submitted;
    return (
      <span className={`dr-badge ${status || 'submitted'}`}>
        <i className={`fas ${s.icon}`}></i>{s.label}
      </span>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  const tabs = isPrivileged
    ? [
        { key: 'all',     icon: 'fa-list-alt',    label: 'All Reports' },
      ]
    : [
        { key: 'submit',  icon: 'fa-paper-plane',  label: 'Submit Report' },
        { key: 'history', icon: 'fa-history',       label: 'My History' },
      ];

  return (
    <div className="daily-report">
      <div className="module-header" style={{ marginBottom: 20 }}>
        <h2><i className="fas fa-file-alt" style={{ color: '#667eea', marginRight: 10 }}></i>Daily Report</h2>
      </div>

      {/* Tabs */}
      <div className="dr-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`dr-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            <i className={`fas ${t.icon}`}></i>{t.label}
          </button>
        ))}
      </div>

      {/* Alert */}
      {alert && (
        <div className={`dr-alert ${alert.type}`}>
          <i className={`fas ${alert.type === 'success' ? 'fa-check-circle' : alert.type === 'info' ? 'fa-info-circle' : 'fa-exclamation-circle'}`}></i>
          {alert.msg}
        </div>
      )}

      {/* ── TAB: SUBMIT ── */}
      {tab === 'submit' && (
        <div className="dr-form-card">
          <h3><i className="fas fa-paper-plane"></i> Aaj ka Report Submit Karo</h3>
          <form onSubmit={handleSubmit}>
            <div className="dr-form-grid">
              <div className="dr-form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={form.date}
                  max={today}
                  onChange={e => setForm({ ...form, date: e.target.value })}
                  required
                />
              </div>
              <div className="dr-form-group">
                <label>Hours Worked</label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  placeholder="e.g. 8"
                  value={form.hoursWorked}
                  onChange={e => setForm({ ...form, hoursWorked: e.target.value })}
                />
              </div>
              <div className="dr-form-group full-width">
                <label>Related Task (Optional)</label>
                <select value={form.taskId} onChange={e => setForm({ ...form, taskId: e.target.value })}>
                  <option value="">— No specific task —</option>
                  {myTasks.map(t => (
                    <option key={t._id} value={t._id}>
                      {t.title} [{t.status}]
                    </option>
                  ))}
                </select>
              </div>
              <div className="dr-form-group full-width">
                <label>Aaj kya kiya? *</label>
                <textarea
                  rows={6}
                  placeholder="Describe what you worked on today..."
                  value={form.report}
                  onChange={e => setForm({ ...form, report: e.target.value })}
                  required
                />
              </div>
            </div>
            <button type="submit" className="dr-submit-btn" disabled={submitting}>
              {submitting
                ? <><i className="fas fa-spinner fa-spin"></i> Submitting...</>
                : <><i className="fas fa-paper-plane"></i> Submit Report</>
              }
            </button>
          </form>
        </div>
      )}

      {/* ── TAB: HISTORY ── */}
      {tab === 'history' && (
        <>
          <div className="dr-filters">
            <div className="dr-filter-group">
              <label>From</label>
              <input type="date" value={histFrom} onChange={e => setHistFrom(e.target.value)} />
            </div>
            <div className="dr-filter-group">
              <label>To</label>
              <input type="date" value={histTo} onChange={e => setHistTo(e.target.value)} />
            </div>
            <button className="btn btn-secondary" onClick={() => fetchHistory(1)}>
              <i className="fas fa-search"></i> Filter
            </button>
          </div>

          <div className="dr-table-wrapper">
            {histLoading ? (
              <div className="dr-loading"><i className="fas fa-spinner fa-spin"></i><p>Loading...</p></div>
            ) : history.length === 0 ? (
              <div className="dr-empty"><i className="fas fa-inbox"></i><p>Koi report nahi mili</p></div>
            ) : (
              <>
                <table className="dr-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Task</th>
                      <th>Report</th>
                      <th>Hours</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(r => (
                      <tr key={r._id}>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtDate(r.date)}</td>
                        <td>{r.task?.title || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                        <td><div className="dr-report-preview">{r.report}</div></td>
                        <td>{r.hoursWorked != null ? `${r.hoursWorked}h` : '—'}</td>
                        <td><StatusBadge status={r.status} /></td>
                        <td style={{ display: 'flex', gap: 6 }}>
                          <button className="dr-btn-view" onClick={() => setViewReport(r)} title="View full report">
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="dr-btn-view"
                            style={{ color: '#ef4444' }}
                            onClick={() => handleDelete(r._id)}
                            title="Delete"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(histMeta.pages > 1) && (
                  <div className="dr-pagination">
                    <span>Total: {histMeta.total} reports</span>
                    <div className="dr-pagination-btns">
                      <button disabled={histPage <= 1} onClick={() => fetchHistory(histPage - 1)}>‹ Prev</button>
                      <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {histPage} / {histMeta.pages}</span>
                      <button disabled={histPage >= histMeta.pages} onClick={() => fetchHistory(histPage + 1)}>Next ›</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── TAB: ALL REPORTS (Admin/HR) ── */}
      {tab === 'all' && isPrivileged && (
        <>
          <div className="dr-filters">
            <div className="dr-filter-group">
              <label>From Date</label>
              <input type="date" value={rptFrom} onChange={e => setRptFrom(e.target.value)} />
            </div>
            <div className="dr-filter-group">
              <label>To Date</label>
              <input type="date" value={rptTo} onChange={e => setRptTo(e.target.value)} />
            </div>
            <div className="dr-filter-group">
              <label>Employee</label>
              <select value={rptEmployee} onChange={e => setRptEmployee(e.target.value)}>
                <option value="">All Employees</option>
                {employees.map(emp => (
                  <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>
                ))}
              </select>
            </div>
            <button className="btn btn-secondary" onClick={() => fetchReports(1)}>
              <i className="fas fa-search"></i> Filter
            </button>
            <button className="btn btn-outline" style={{ border: '1.5px solid #e5e7eb', background: 'white', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}
              onClick={() => { setRptFrom(''); setRptTo(''); setRptEmployee(''); setTimeout(() => fetchReports(1), 50); }}>
              Clear
            </button>
          </div>

          <div className="dr-table-wrapper">
            {rptLoading ? (
              <div className="dr-loading"><i className="fas fa-spinner fa-spin"></i><p>Loading...</p></div>
            ) : reports.length === 0 ? (
              <div className="dr-empty"><i className="fas fa-inbox"></i><p>Koi report nahi mili</p></div>
            ) : (
              <>
                <table className="dr-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Emp ID</th>
                      <th>Date</th>
                      <th>Task</th>
                      <th>Report</th>
                      <th>Hours</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map(r => (
                      <tr key={r._id}>
                        <td style={{ fontWeight: 600 }}>{r.employee?.name || '—'}</td>
                        <td style={{ color: '#6b7280', fontSize: 13 }}>{r.employee?.employeeId || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                        <td>{r.task?.title || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                        <td><div className="dr-report-preview">{r.report}</div></td>
                        <td>{r.hoursWorked != null ? `${r.hoursWorked}h` : '—'}</td>
                        <td><StatusBadge status={r.status} /></td>
                        <td>
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                            <button className="dr-btn-view" onClick={() => setViewReport(r)} title="View">
                              <i className="fas fa-eye"></i>
                            </button>
                            {r.status !== 'approved' && (
                              <button
                                className="dr-btn-review approve"
                                onClick={() => { setReviewModal(r); setReviewStatus('approved'); setReviewNote(''); }}
                              >
                                <i className="fas fa-check"></i> Approve
                              </button>
                            )}
                            {r.status === 'submitted' && (
                              <button
                                className="dr-btn-review review"
                                onClick={() => { setReviewModal(r); setReviewStatus('reviewed'); setReviewNote(''); }}
                              >
                                <i className="fas fa-eye"></i> Review
                              </button>
                            )}
                            <button
                              className="dr-btn-view"
                              style={{ color: '#ef4444' }}
                              onClick={() => handleDelete(r._id)}
                              title="Delete"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(rptMeta.pages > 1) && (
                  <div className="dr-pagination">
                    <span>Total: {rptMeta.total} reports</span>
                    <div className="dr-pagination-btns">
                      <button disabled={rptPage <= 1} onClick={() => fetchReports(rptPage - 1)}>‹ Prev</button>
                      <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {rptPage} / {rptMeta.pages}</span>
                      <button disabled={rptPage >= rptMeta.pages} onClick={() => fetchReports(rptPage + 1)}>Next ›</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── DETAIL VIEW MODAL ── */}
      {viewReport && (
        <div className="dr-detail-overlay" onClick={() => setViewReport(null)}>
          <div className="dr-detail-modal" onClick={e => e.stopPropagation()}>
            <h3>
              Report Details
              <button onClick={() => setViewReport(null)}>×</button>
            </h3>
            {isPrivileged && (
              <>
                <div className="dr-detail-row">
                  <span className="dr-detail-label">Employee</span>
                  <span className="dr-detail-value">{viewReport.employee?.name || '—'} ({viewReport.employee?.employeeId || '—'})</span>
                </div>
              </>
            )}
            <div className="dr-detail-row">
              <span className="dr-detail-label">Date</span>
              <span className="dr-detail-value">{fmtDate(viewReport.date)}</span>
            </div>
            <div className="dr-detail-row">
              <span className="dr-detail-label">Task</span>
              <span className="dr-detail-value">{viewReport.task?.title || '— No specific task —'}</span>
            </div>
            <div className="dr-detail-row">
              <span className="dr-detail-label">Hours Worked</span>
              <span className="dr-detail-value">{viewReport.hoursWorked != null ? `${viewReport.hoursWorked} hours` : '—'}</span>
            </div>
            <div className="dr-detail-row">
              <span className="dr-detail-label">Status</span>
              <span className="dr-detail-value"><StatusBadge status={viewReport.status} /></span>
            </div>
            <div className="dr-detail-row" style={{ flexDirection: 'column', gap: 6 }}>
              <span className="dr-detail-label">Report</span>
              <div style={{ background: '#f9fafb', padding: 16, borderRadius: 10, fontSize: 14, lineHeight: 1.7, color: '#374151', border: '1px solid #e5e7eb', whiteSpace: 'pre-wrap' }}>
                {viewReport.report}
              </div>
            </div>
            {viewReport.reviewer && (
              <div style={{ marginTop: 20, padding: 16, background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#065f46', marginBottom: 6 }}>
                  <i className="fas fa-user-check" style={{ marginRight: 6 }}></i>
                  Reviewed by {viewReport.reviewer?.name || 'HR'}
                </p>
                {viewReport.reviewNote && <p style={{ fontSize: 14, color: '#374151' }}>{viewReport.reviewNote}</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── REVIEW MODAL ── */}
      {reviewModal && (
        <div className="dr-detail-overlay" onClick={() => setReviewModal(null)}>
          <div className="dr-detail-modal" onClick={e => e.stopPropagation()}>
            <h3>
              Review Report
              <button onClick={() => setReviewModal(null)}>×</button>
            </h3>
            <div className="dr-detail-row">
              <span className="dr-detail-label">Employee</span>
              <span className="dr-detail-value">{reviewModal.employee?.name}</span>
            </div>
            <div className="dr-detail-row">
              <span className="dr-detail-label">Date</span>
              <span className="dr-detail-value">{fmtDate(reviewModal.date)}</span>
            </div>
            <div style={{ background: '#f9fafb', padding: 14, borderRadius: 10, marginBottom: 20, fontSize: 14, lineHeight: 1.6, border: '1px solid #e5e7eb' }}>
              {reviewModal.report}
            </div>
            <div className="dr-review-section">
              <h4>Review Action</h4>
              <div className="dr-form-group" style={{ marginBottom: 16 }}>
                <label>Status</label>
                <select value={reviewStatus} onChange={e => setReviewStatus(e.target.value)} style={{ padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}>
                  <option value="reviewed">Reviewed</option>
                  <option value="approved">Approved</option>
                </select>
              </div>
              <div className="dr-form-group" style={{ marginBottom: 20 }}>
                <label>Review Note (optional)</label>
                <textarea
                  rows={3}
                  placeholder="e.g. Good work! Keep it up."
                  value={reviewNote}
                  onChange={e => setReviewNote(e.target.value)}
                  style={{ padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleReview} className="dr-submit-btn">
                  <i className="fas fa-check"></i> Submit Review
                </button>
                <button onClick={() => setReviewModal(null)} style={{ padding: '10px 20px', border: '1.5px solid #e5e7eb', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 14 }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyReport;
